/**
 * Cohort 1 admin panel — Lead Captures.
 *
 * Surfaces anonymous-visitor leads from /api/admin/lead-captures.
 *   - kind="buyers_checklist" → home-hero email-gated PDF
 *   - kind="soft_paywall"     → calculator unlock email
 *
 * Includes a totals tile + a per-row table with kind / source / email
 * / opt-in / created. No mutations — pure read-only inbox.
 */
import { useCallback, useEffect, useState } from "react";
import { Mail, Inbox, Loader2, RefreshCw, CheckCircle2, AlertCircle } from "lucide-react";
import { adminListLeadCaptures } from "@/lib/api";

const KIND_OPTIONS = [
    { value: "", label: "All sources" },
    { value: "buyers_checklist", label: "Buyer's Checklist" },
    { value: "soft_paywall", label: "Calculator paywall" },
];

function relativeTime(iso) {
    if (!iso) return "—";
    try {
        const t = new Date(iso).getTime();
        if (!Number.isFinite(t)) return iso;
        const diff = Date.now() - t;
        if (diff < 60_000) return "just now";
        if (diff < 3600_000) return `${Math.floor(diff / 60_000)} min ago`;
        if (diff < 86_400_000) return `${Math.floor(diff / 3600_000)} h ago`;
        return `${Math.floor(diff / 86_400_000)} d ago`;
    } catch {
        return iso;
    }
}

export default function LeadCapturesPanel() {
    const [items, setItems] = useState([]);
    const [totals, setTotals] = useState({});
    const [kind, setKind] = useState("");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const refresh = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const r = await adminListLeadCaptures(kind ? { kind } : {});
            setItems(r?.items || []);
            setTotals(r?.totals_by_kind_source || {});
        } catch (e) {
            setError(
                e?.response?.data?.detail ||
                e?.message ||
                "Could not load lead captures.",
            );
        } finally {
            setLoading(false);
        }
    }, [kind]);

    useEffect(() => {
        refresh();
    }, [refresh]);

    const checklistCount = Object.entries(totals)
        .filter(([k]) => k.startsWith("buyers_checklist::"))
        .reduce((a, [, n]) => a + (Number(n) || 0), 0);
    const paywallCount = Object.entries(totals)
        .filter(([k]) => k.startsWith("soft_paywall::"))
        .reduce((a, [, n]) => a + (Number(n) || 0), 0);

    return (
        <section
            className="bl-card p-6 sm:p-8"
            data-testid="lead-captures-panel"
        >
            <div className="flex items-start justify-between flex-wrap gap-4 mb-5">
                <div className="flex items-start gap-3">
                    <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-[var(--bl-tint)] text-bl-primary shrink-0">
                        <Inbox className="w-5 h-5" />
                    </span>
                    <div>
                        <h2 className="font-display font-semibold text-xl text-bl">
                            Cohort 1 — Lead Captures
                        </h2>
                        <p className="mt-1 text-sm text-bl-muted leading-relaxed max-w-xl">
                            Anonymous visitors who handed over an email for the
                            checklist PDF or to unlock a free calculator.
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <select
                        value={kind}
                        onChange={(e) => setKind(e.target.value)}
                        className="bl-input text-sm py-2"
                        data-testid="lead-captures-filter"
                    >
                        {KIND_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value}>
                                {o.label}
                            </option>
                        ))}
                    </select>
                    <button
                        type="button"
                        onClick={refresh}
                        disabled={loading}
                        className="bl-btn-secondary text-sm gap-1.5"
                        data-testid="lead-captures-refresh"
                    >
                        {loading ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                            <RefreshCw className="w-3.5 h-3.5" />
                        )}
                        Refresh
                    </button>
                </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-5">
                <KpiTile
                    label="Checklist downloads"
                    value={checklistCount}
                    testid="kpi-checklist"
                />
                <KpiTile
                    label="Paywall unlocks"
                    value={paywallCount}
                    testid="kpi-paywall"
                />
                <KpiTile
                    label="Total leads"
                    value={checklistCount + paywallCount}
                    testid="kpi-total"
                />
            </div>

            {error && (
                <div
                    className="rounded-lg border border-[var(--bl-rust)] bg-[var(--bl-rust-tint)] p-4 mb-5 text-sm text-[var(--bl-rust)] inline-flex items-start gap-2"
                    data-testid="lead-captures-error"
                >
                    <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                    {error}
                </div>
            )}

            {!error && items.length === 0 && !loading && (
                <div
                    className="rounded-lg border border-dashed border-bl p-8 text-center text-sm text-bl-muted"
                    data-testid="lead-captures-empty"
                >
                    No captures yet. The home hero CTA and calculator soft-paywall
                    will feed this inbox.
                </div>
            )}

            {items.length > 0 && (
                <div className="overflow-x-auto">
                    <table
                        className="w-full text-sm border-collapse"
                        data-testid="lead-captures-table"
                    >
                        <thead>
                            <tr className="text-left text-xs uppercase tracking-wide text-bl-muted border-b border-bl">
                                <th className="py-2 pr-3">Email</th>
                                <th className="py-2 pr-3">Kind</th>
                                <th className="py-2 pr-3">Source</th>
                                <th className="py-2 pr-3">Tool</th>
                                <th className="py-2 pr-3">Opt-in</th>
                                <th className="py-2 pr-3">When</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((r) => (
                                <tr
                                    key={r.lead_id || r.id}
                                    className="border-b border-bl/30 hover:bg-bl-bg/40"
                                    data-testid={`lead-${r.lead_id || r.id}`}
                                >
                                    <td className="py-2 pr-3 font-mono text-bl">
                                        <span className="inline-flex items-center gap-1.5">
                                            <Mail className="w-3.5 h-3.5 text-bl-primary" />
                                            {r.email}
                                        </span>
                                    </td>
                                    <td className="py-2 pr-3 text-bl">
                                        {r.kind === "buyers_checklist" ? "Checklist" : "Paywall"}
                                    </td>
                                    <td className="py-2 pr-3 text-bl-muted">{r.source || "—"}</td>
                                    <td className="py-2 pr-3 text-bl-muted">
                                        {r.tool_slug || "—"}
                                    </td>
                                    <td className="py-2 pr-3">
                                        {r.marketing_opt_in ? (
                                            <span className="inline-flex items-center gap-1 text-xs text-[var(--bl-moss)]">
                                                <CheckCircle2 className="w-3.5 h-3.5" />
                                                opted in
                                            </span>
                                        ) : (
                                            <span className="text-xs text-bl-muted">—</span>
                                        )}
                                    </td>
                                    <td className="py-2 pr-3 text-bl-muted whitespace-nowrap">
                                        {relativeTime(r.created_at)}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </section>
    );
}

function KpiTile({ label, value, testid }) {
    return (
        <div
            className="rounded-lg border border-bl bg-bl-surface p-4"
            data-testid={testid}
        >
            <div className="text-xs uppercase tracking-wide text-bl-muted">
                {label}
            </div>
            <div className="mt-1 font-display font-semibold text-2xl text-bl">
                {value}
            </div>
        </div>
    );
}
